// Per-chat memory of the agent + model a chat was last USED with — i.e. the
// agent picker + model/thinking/fast selection that was active when the user
// last sent a message in that chat. Keyed by the chat's runId and persisted in
// localStorage so that switching back to a previous chat can restore exactly
// what that chat was last used with. This is the ONLY thing that changes the
// selection on its own: starting a new chat or reconnecting keeps whatever is
// currently selected.
export type ChatSelection = {
  agentId?: string;
  model?: string;
  thinking?: string;
  fast?: boolean;
  // Max Mode (Cursor Composer 2.5 only). Independent of fast — they combine.
  max?: boolean;
  // Additional models picked alongside the primary one. When non-empty the next
  // send fans out as a consensus turn — one subchat per model — instead of a
  // single-model turn. Each entry is a `model[:thinking]` encoding (same shape
  // as the dropdown's option values).
  extraModels?: string[];
};

const KEY = 'altY.chatSelection.v1';
// Bound the map so a long-lived browser can't grow it without limit. runIds are
// ascending integers, and JS orders integer-like object keys numerically, so
// the first keys are the oldest — drop those when over the cap.
const MAX_ENTRIES = 300;

function readAll(): Record<string, ChatSelection> {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, ChatSelection>) : {};
  } catch {
    return {};
  }
}

function writeAll(map: Record<string, ChatSelection>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* quota or disabled storage — ignore */
  }
}

export function getChatSelection(runId: number): ChatSelection | null {
  const sel = readAll()[String(runId)];
  return sel && typeof sel === 'object' ? sel : null;
}

export function hasChatSelection(runId: number): boolean {
  return !!readAll()[String(runId)];
}

export function setChatSelection(runId: number, sel: ChatSelection): void {
  const map = readAll();
  map[String(runId)] = sel;
  const keys = Object.keys(map);
  if (keys.length > MAX_ENTRIES) {
    for (const k of keys.slice(0, keys.length - MAX_ENTRIES)) delete map[k];
  }
  writeAll(map);
}
